"use client";

import { useState } from "react";
import Link from "next/link";
import { HeaderCtaLink } from "@/components/marketing/header-cta-link";

// Base navLinks shared by every render. The flag-gated Site Control entry is
// appended in the component from the server-resolved `siteControlEnabled` prop
// (LAND-08) — kept in LOCKSTEP with the desktop nav in app/(marketing)/layout.tsx
// (Anti-Pattern 4). The boolean is resolved server-side, so when the flag is off
// the link is absent from the rendered HTML, not merely hidden client-side.
const baseNavLinks = [
  { href: "/features", label: "Features" },
  { href: "/business", label: "Business" },
  { href: "/integrations", label: "Integrations" },
  { href: "/how-it-works", label: "How it works" },
  { href: "/who-its-for", label: "Who it's for" },
  { href: "/pricing", label: "Pricing" },
  { href: "/blog", label: "Blog" },
  { href: "/guides", label: "Guides" },
  { href: "/demo", label: "Demo" },
];

export function MobileNav({
  siteControlEnabled = false,
}: {
  siteControlEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Insert the flag-gated Site Control link after "Pricing", matching the
  // desktop nav position (LOCKSTEP with app/(marketing)/layout.tsx — Anti-Pattern
  // 4). Built per-render from the server-resolved prop, so it's absent from
  // flag-off HTML. The Principal Contractors ICP page is intentionally NOT in
  // the top nav (it lives in the footer only) — keeping the bar uncrowded.
  const navLinks = siteControlEnabled
    ? [
        ...baseNavLinks.slice(0, 6),
        { href: "/site-control", label: "Site Control" },
        ...baseNavLinks.slice(6),
      ]
    : baseNavLinks;

  return (
    <>
      {/* Hamburger button — mobile only */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-muted hover:bg-surface-muted md:hidden"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
      >
        {open ? (
          /* X icon */
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        ) : (
          /* Hamburger icon */
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        )}
      </button>

      {/* Mobile dropdown */}
      {open && (
        <div className="absolute inset-x-0 top-full z-50 border-b border-border bg-surface shadow-lg md:hidden">
          <nav className="px-4 py-4">
            <ul className="space-y-1">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="block min-h-[44px] rounded-lg px-3 py-2.5 text-base font-medium text-foreground/80 hover:bg-surface-muted hover:text-navy"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2 border-t border-border">
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-base font-medium text-text-secondary hover:bg-surface-muted"
                >
                  Log in
                </Link>
              </li>
              <li>
                <HeaderCtaLink
                  ctaLocation="mobile-menu"
                  onClick={() => setOpen(false)}
                  className="block rounded-lg bg-accent px-3 py-2.5 text-center text-base font-bold text-white hover:bg-accent-dark"
                >
                  Get started
                </HeaderCtaLink>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </>
  );
}
